import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import './MarkdownStyles.css';

// Import our custom components
import EnhancedMessage from './components/EnhancedMessage';
import Suggestions from './components/Suggestions';

// Import utilities
import { 
  isGeekCultureQuery, 
  categorizeGeekQuery, 
  getRedirectWithSuggestions 
} from './utils/geekGuardrails';

import {
  saveChatHistory,
  getChatHistory,
  generateChatId
} from './utils/storage';

import {
  detectCategories,
  generateCategoryPrompt,
  getCategoryTheme
} from './utils/geekCategories';

// Configuration for the API
const API_CONFIG = {
  baseURL: 'https://gkc-9ml4oetd9-coat1099s-projects.vercel.app/api/chat',
  model: 'gpt-4o-2024-08-06',
  defaultParams: {
    temperature: 0.8,
    max_tokens: 1000,
    frequency_penalty: 0.2,
    presence_penalty: 0.2,
    top_p: 0.95
  }
};

function App() {
  const [input, setInput] = useState('');
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(true); // Track if backend is connected
  const [currentChatId, setCurrentChatId] = useState(generateChatId());
  const [categoryTheme, setCategoryTheme] = useState({ primaryColor: '#6b5cff', icon: 'default' });
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  
  const messagesEndRef = useRef(null);
  
  // Load chat history on component mount
  useEffect(() => {
    const chatHistory = getChatHistory(currentChatId);
    if (chatHistory) {
      setResponses(chatHistory.messages);
    }
    
    // Extract the base URL from the API_CONFIG.baseURL
    const apiBaseUrl = API_CONFIG.baseURL.replace('/api/chat', '');
    
    // Check if backend is available
    fetch(`${apiBaseUrl}/api/health`)
      .then(response => {
        if (response.ok) {
          setIsConnected(true);
        } else {
          setIsConnected(false);
        }
      })
      .catch(() => {
        setIsConnected(false);
      });
  }, [currentChatId]);
  
  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    
    // Save chat history when responses change
    if (responses.length > 0) {
      saveChatHistory(currentChatId, responses);
    }
  }, [responses, currentChatId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    // Add user message to responses
    const userMessage = input;
    setResponses(prev => [...prev, { role: 'user', content: userMessage }]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Prepare messages array
      const messages = [
        ...responses.map(msg => ({ role: msg.role, content: msg.content })),
        { role: 'user', content: userMessage }
      ];
      
      // Use our backend API
      const response = await fetch(API_CONFIG.baseURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: messages,
          model: API_CONFIG.model
        })
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      const assistantResponse = data.choices[0].message.content;
      
      // Add assistant response to the chat
      setResponses(prev => [...prev, { role: 'assistant', content: assistantResponse }]);
    } catch (error) {
      console.error('Error:', error);
      setResponses(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error processing your request. Please check if the backend server is running.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setResponses([]);
    setCurrentChatId(generateChatId());
    setCategoryTheme({ primaryColor: '#6b5cff', icon: 'default' });
  };
  
  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
  };

  // Filter messages based on search query
  const filteredResponses = responses.filter(message => 
    message.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };

  // Add copy message handler
  const handleCopyMessage = (content, messageId) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    });
  };

  // Add highlight function
  const highlightSearchTerm = (text) => {
    if (!searchQuery) return text;
    
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === searchQuery.toLowerCase() 
        ? <span key={i} className="search-highlight">{part}</span>
        : part
    );
  };

  // If backend is not connected, show a warning
  if (!isConnected) {
    return (
      <div className="app-container">
        <div className="sidebar">
          <div className="sidebar-header">
            <h1>GeekClaude</h1>
            <p>Your AI guide to comics, anime, manga and geek culture</p>
          </div>
        </div>
        
        <div className="main-content">
          <div className="api-key-container">
            <h2>Backend Connection Error</h2>
            <p>Unable to connect to the backend server. Please make sure it's running with:</p>
            <pre>npm run server</pre>
            <p>And try refreshing this page.</p>
            <button onClick={() => window.location.reload()}>Refresh</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className={`sidebar ${isSidebarExpanded ? 'expanded' : ''}`}>
        <div className="sidebar-header">
          <h1>GeekClaude</h1>
          <p>Your AI guide to comics, anime, manga and geek culture</p>
        </div>
        
        <h2>About GeekClaude</h2>
        
        <div className="sidebar-section">
          <h3>What I Can Do</h3>
          <ul>
            <li>Answer questions about comic books, manga, and anime</li>
            <li>Explain character histories and relationships</li>
            <li>Discuss TV shows like Game of Thrones, The Walking Dead</li>
            <li>Cover video game lore and storylines</li>
            <li>Compare different versions of characters and stories</li>
          </ul>
        </div>

        <div className="sidebar-section">
          <h3>Supported Franchises</h3>
          <ul>
            <li>Comics: Marvel, DC, Image, Dark Horse</li>
            <li>Fantasy: Game of Thrones, Lord of the Rings, Harry Potter</li>
            <li>Sci-Fi: Star Wars, Star Trek, The Matrix</li>
            <li>Games: Halo, Mass Effect, The Last of Us, God of War</li>
            <li>Anime/Manga: All major series and creators</li>
          </ul>
        </div>

        <div className="sidebar-section">
          <h3>Tips for Better Results</h3>
          <ul>
            <li>Be specific with character names and series</li>
            <li>Ask about relationships and story arcs</li>
            <li>Mention if you want book or show information</li>
            <li>Include timeline/universe details if relevant</li>
          </ul>
        </div>
      </div>

      <div className="main-content">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search in chat history..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="chat-container">
          {responses.length === 0 ? (
            <Suggestions onSuggestionClick={handleSuggestionClick} />
          ) : (
            filteredResponses.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                <EnhancedMessage 
                  role={message.role}
                  content={highlightSearchTerm(message.content)}
                  isLoading={false}
                  theme={categoryTheme}
                />
                {message.role === 'assistant' && (
                  <div className="message-actions">
                    <button
                      className="copy-button"
                      onClick={() => handleCopyMessage(message.content, index)}
                    >
                      {copiedMessageId === index ? '✓ Copied!' : '📋 Copy'}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
          {isLoading && (
            <EnhancedMessage role="assistant" isLoading={true} content="" theme={categoryTheme} />
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <form className="input-form" onSubmit={handleSubmit}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about comics, anime, manga..."
            disabled={isLoading}
          />
          <button type="submit" disabled={isLoading || !input.trim()}>
            {isLoading ? 'Thinking...' : 'Ask'}
          </button>
          <button type="button" onClick={clearChat} className="clear-button">
            New Chat
          </button>
        </form>
      </div>

      {/* Mobile sidebar toggle button */}
      <button className="toggle-sidebar" onClick={toggleSidebar}>
        {isSidebarExpanded ? '✕' : 'ℹ️'}
      </button>
    </div>
  );
}

export default App; 